import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../../utils/apiConfig';
import { DateField, FormDialog, InlineMessage } from '../ui';
import {
  BrandAuthorization,
  BrandAuthorizationDesignation,
  BrandDesignationSource,
  BrandDesignationTier,
  BrandProductCategory,
  DESIGNATION_SOURCE_LABELS,
  DESIGNATION_TIERS,
  PRODUCT_CATEGORIES,
  isMachineDesignation,
  levelTakesProof,
} from '../../utils/brandAuthorization';
import BrandDesignationChips from './BrandDesignationChips';

interface DraftDesignation {
  tier: BrandDesignationTier;
  brandTierCode: string;
  categoryScope: BrandProductCategory[];
  source: Exclude<BrandDesignationSource, 'directory' | 'locator'>;
  sourceUrl: string;
  effectiveFrom: string;
  effectiveTo: string;
  note: string;
}

const day = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : '');

const toDraft = (d: BrandAuthorizationDesignation): DraftDesignation => ({
  tier: d.tier,
  brandTierCode: d.brandTierCode || '',
  categoryScope: d.categoryScope || [],
  source: d.source === 'proof' || d.source === 'self_declared' ? d.source : 'admin',
  sourceUrl: d.sourceUrl || '',
  effectiveFrom: day(d.effectiveFrom),
  effectiveTo: day(d.effectiveTo),
  note: d.note || '',
});

const emptyDraft = (): DraftDesignation => ({
  tier: 'premier',
  brandTierCode: '',
  categoryScope: [],
  source: 'admin',
  sourceUrl: '',
  effectiveFrom: '',
  effectiveTo: '',
  note: '',
});

async function readError(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
}

interface BrandDesignationsDialogProps {
  organizationId: string;
  record: BrandAuthorization | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Alex records the tiers a brand gives this store (Sony CIS Diamond from a
 * letter, an EDU reseller confirmation). Designations Motzu read off the
 * brand's dealer list or locator are shown but not editable: the next check
 * rewrites them. Saving never changes the level or the verification.
 */
const BrandDesignationsDialog: React.FC<BrandDesignationsDialogProps> = ({ organizationId, record, onClose, onSaved }) => {
  const [drafts, setDrafts] = useState<DraftDesignation[]>([]);
  const machine = (record?.designations || []).filter(isMachineDesignation);
  const canHold = record ? levelTakesProof(record.level) : false;

  const save = useMutation({
    mutationFn: async () => {
      if (!record) throw new Error('Nothing to save');
      const backwards = drafts.find(d => d.effectiveFrom && d.effectiveTo && d.effectiveFrom > d.effectiveTo);
      if (backwards) throw new Error('A designation cannot end before it starts');
      const response = await makeApiCall(
        `/admin/brand-authorizations/organizations/${organizationId}/${encodeURIComponent(record.brandId)}/designations`,
        {
          method: 'PUT',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            designations: drafts.map(d => ({
              tier: d.tier,
              brandTierCode: d.brandTierCode.trim() || null,
              categoryScope: d.categoryScope,
              source: d.source,
              sourceUrl: d.sourceUrl.trim() || null,
              effectiveFrom: d.effectiveFrom || null,
              effectiveTo: d.effectiveTo || null,
              note: d.note.trim() || null,
            })),
          }),
        },
      );
      if (!response.ok) throw new Error(await readError(response, 'Failed to save designations'));
    },
    onSuccess: () => onSaved(),
  });

  const { reset } = save;
  useEffect(() => {
    setDrafts((record?.designations || []).filter(d => !isMachineDesignation(d)).map(toDraft));
    reset();
  }, [record, reset]);

  const update = (index: number, patch: Partial<DraftDesignation>) =>
    setDrafts(current => current.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  return (
    <FormDialog
      open={!!record}
      title={record ? `${record.brandName} designations` : ''}
      onClose={onClose}
      onSubmit={() => save.mutate()}
      submitLabel="Save designations"
      submitDisabled={!canHold && drafts.length > 0}
      loading={save.isPending}
      maxWidth="md"
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Tiers the brand gives this store on top of its authorization level, such as a premier or education reseller
          status. They describe the store; they never change its level, verification or what it can see.
        </Typography>
        {!canHold && (
          <InlineMessage severity="info">
            This claim is unauthorized or a former dealer, so it carries no designations. Saving an empty list clears any left over.
          </InlineMessage>
        )}
        {machine.length > 0 && (
          <Box>
            <Typography variant="overline" color="text.secondary">
              From the brand's list or locator
            </Typography>
            <BrandDesignationChips designations={machine} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Rewritten by the next dealer-list or locator check, so they are not edited here.
            </Typography>
          </Box>
        )}
        <Box>
          <Typography variant="overline" color="text.secondary">
            Recorded by Motzu
          </Typography>
          {drafts.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              None recorded. Add one when a letter, certificate or the brand rep confirms a tier.
            </Typography>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {drafts.map((d, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <TextField
                  select
                  size="small"
                  label="Tier"
                  value={d.tier}
                  onChange={e => update(index, { tier: e.target.value as BrandDesignationTier })}
                  sx={{ flex: '1 1 200px' }}
                  helperText={DESIGNATION_TIERS.find(t => t.value === d.tier)?.description}
                >
                  {DESIGNATION_TIERS.map(t => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Brand's own name for it"
                  placeholder="e.g. CIS Diamond, NPD"
                  value={d.brandTierCode}
                  onChange={e => update(index, { brandTierCode: e.target.value })}
                  inputProps={{ maxLength: 80 }}
                  sx={{ flex: '1 1 180px' }}
                />
                <TextField
                  select
                  size="small"
                  label="Product categories"
                  value={d.categoryScope}
                  onChange={e => {
                    const value = e.target.value as unknown;
                    update(index, { categoryScope: (Array.isArray(value) ? value : String(value).split(',')) as BrandProductCategory[] });
                  }}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: selected =>
                      (selected as string[]).length === 0 ? (
                        'Every category'
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map(value => (
                            <Chip key={value} size="small" label={PRODUCT_CATEGORIES.find(c => c.value === value)?.label || value} />
                          ))}
                        </Box>
                      ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: '1 1 200px' }}
                >
                  {PRODUCT_CATEGORIES.map(c => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Evidence"
                  value={d.source}
                  onChange={e => update(index, { source: e.target.value as DraftDesignation['source'] })}
                  sx={{ flex: '1 1 160px' }}
                >
                  <MenuItem value="admin">{DESIGNATION_SOURCE_LABELS.admin}</MenuItem>
                  <MenuItem value="proof">{DESIGNATION_SOURCE_LABELS.proof}</MenuItem>
                  {d.source === 'self_declared' && <MenuItem value="self_declared">{DESIGNATION_SOURCE_LABELS.self_declared}</MenuItem>}
                </TextField>
                <DateField
                  label="Starts (optional)"
                  value={d.effectiveFrom}
                  onChange={value => update(index, { effectiveFrom: value })}
                  sx={{ flex: '1 1 160px' }}
                />
                <DateField
                  label="Ends (optional)"
                  value={d.effectiveTo}
                  min={d.effectiveFrom || undefined}
                  onChange={value => update(index, { effectiveTo: value })}
                  helperText="Removed the day after it ends"
                  sx={{ flex: '1 1 160px' }}
                />
                <TextField
                  size="small"
                  label="Source link (optional)"
                  placeholder="https://"
                  value={d.sourceUrl}
                  onChange={e => update(index, { sourceUrl: e.target.value })}
                  sx={{ flex: '2 1 260px' }}
                />
                <TextField
                  size="small"
                  label="Note (optional)"
                  value={d.note}
                  onChange={e => update(index, { note: e.target.value })}
                  inputProps={{ maxLength: 500 }}
                  sx={{ flex: '2 1 260px' }}
                />
                <Tooltip title="Remove this designation">
                  <IconButton aria-label="Remove designation" onClick={() => setDrafts(current => current.filter((_, i) => i !== index))}>
                    <DeleteOutline />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
          <Tooltip title={canHold ? '' : 'Unauthorized and former-dealer claims carry no designations'}>
            <span>
              <Button
                size="small"
                startIcon={<Add />}
                sx={{ mt: 1 }}
                disabled={!canHold || drafts.length >= 20}
                onClick={() => setDrafts(current => [...current, emptyDraft()])}
              >
                Add designation
              </Button>
            </span>
          </Tooltip>
        </Box>
        {save.isError && <InlineMessage severity="error">{(save.error as Error).message}</InlineMessage>}
      </Stack>
    </FormDialog>
  );
};

export default BrandDesignationsDialog;
